import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { formatDate, formatDisplayDate } from '../utils/dateUtils';
import Snackbar from '../components/Snackbar';
import { useSnackbar } from '../hooks/useSnackbar';
import './Banners.css';

const Banners = () => {
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    linkUrl: '',
    startDate: '',
    endDate: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);

  useEffect(() => {
    fetchBanners();
    
    // Set up interval to check and update banner statuses every hour
    const intervalId = setInterval(() => {
      
      fetchBanners();
    }, 60 * 60 * 1000); // Check every hour
    
    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);

      // Fetch all documents from banners collection
      const querySnapshot = await getDocs(collection(db, 'banners'));
      
      const bannersList = [];
      const now = new Date();
      
      for (const docSnapshot of querySnapshot.docs) {
        const data = docSnapshot.data();

        // Only include documents that have the required fields
        if (data.createdAt && data.url) {
          const banner = {
            id: docSnapshot.id,
            createdAt: data.createdAt,
            endDate: data.endDate || '',
            link: data.link || '',
            name: data.name || '',
            size: data.size || 0,
            startDate: data.startDate || '',
            storagePath: data.storagePath || '',
            type: data.type || '',
            status: data.status || 'active',
            url: data.url || ''
          };
          
          // Check if banner is active based on dates
          const startDate = data.startDate ? new Date(data.startDate) : null;
          const endDate = data.endDate ? new Date(data.endDate) : null;
          
          // Set end date to end of day for proper comparison
          if (endDate) {
            endDate.setHours(23, 59, 59, 999);
          }
          
          // Set start date to beginning of day for proper comparison
          if (startDate) {
            startDate.setHours(0, 0, 0, 0);
          }
          
          let shouldBeActive = true;
          if (startDate && now < startDate) shouldBeActive = false;
          if (endDate && now > endDate) shouldBeActive = false;
          
          const newStatus = shouldBeActive ? 'active' : 'inactive';

          // Update status in database if it has changed
          if (banner.status !== newStatus) {
            try {
              await updateDoc(doc(db, 'banners', docSnapshot.id), {
                status: newStatus
              });
              banner.status = newStatus;
              
            } catch (updateError) {
              console.error(`Error updating banner ${docSnapshot.id} status:`, updateError);
            }
          }
          
          bannersList.push(banner);
        }
      }
      
      // Sort by createdAt (newest first)
      bannersList.sort((a, b) => {
        const aDate = new Date(a.createdAt);
        const bDate = new Date(b.createdAt);
        return bDate - aDate;
      });

      setBanners(bannersList);
    } catch (error) {
      console.error('Error fetching banners:', error);
      // Set empty array on error to prevent UI issues
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingBanner(null);
    setFormData({
      linkUrl: '',
      startDate: '',
      endDate: ''
    });
    setSelectedImage(null);
    setImagePreview('');
    setShowForm(true);
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      linkUrl: banner.link || '',
      startDate: banner.startDate || '',
      endDate: banner.endDate || ''
    });
    setSelectedImage(null);
    setImagePreview(banner.url || '');
    setShowForm(true);
  };

  const handleDeleteClick = (banner) => {
    setBannerToDelete(banner);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bannerToDelete) return;

    try {
      await deleteDoc(doc(db, 'banners', bannerToDelete.id));
      await fetchBanners();
      setShowDeleteConfirm(false);
      setBannerToDelete(null);
      showSnackbar('Banner deleted successfully!', 'success');
      
    } catch (error) {
      console.error('Error deleting banner:', error);
      showSnackbar('Error deleting banner. Please try again.', 'error');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setBannerToDelete(null);
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    
    try {
      setUploading(true);
      const timestamp = Date.now();
      const fileName = `banners/${timestamp}-${file.name}`;
      const storageRef = ref(storage, fileName);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        name: file.name,
        size: file.size,
        type: file.type,
        storagePath: fileName
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const computeBannerStatus = (startDateValue, endDateValue) => {
    const now = new Date();
    const start = parseBannerDate(startDateValue, { endOfDay: false });
    const end = parseBannerDate(endDateValue, { endOfDay: true });

    if (start && now < start) return 'inactive';
    if (end && now > end) return 'inactive';
    return 'active';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      
      let imageData = null;
      
      // Upload new image if selected
      if (selectedImage) {
        imageData = await uploadImage(selectedImage);
      } else if (editingBanner && editingBanner.url) {
        // Keep existing image data for edit mode
        imageData = {
          url: editingBanner.url,
          name: editingBanner.name,
          size: editingBanner.size,
          type: editingBanner.type,
          storagePath: editingBanner.storagePath
        };
      }
      
      // Only save the exact data structure specified
      const computedStatus = computeBannerStatus(formData.startDate, formData.endDate);

      const bannerData = {
        createdAt: editingBanner ? editingBanner.createdAt : formatDate(new Date()),
        endDate: formData.endDate,
        link: formData.linkUrl,
        startDate: formData.startDate,
        status: computedStatus,
        ...(imageData && {
          name: imageData.name,
          size: imageData.size,
          type: imageData.type,
          storagePath: imageData.storagePath,
          url: imageData.url
        })
      };

      if (editingBanner) {
        await updateDoc(doc(db, 'banners', editingBanner.id), bannerData);
        setBanners(banners.map(banner => 
          banner.id === editingBanner.id ? { ...banner, ...bannerData } : banner
        ));
      } else {
        const docRef = await addDoc(collection(db, 'banners'), bannerData);
        setBanners([{ id: docRef.id, ...bannerData }, ...banners]);
      }

      setShowForm(false);
      setFormData({
        title: '',
        description: '',
        imageUrl: '',
        linkUrl: '',
        position: '',
        status: 'active',
        startDate: '',
        endDate: ''
      });
      setSelectedImage(null);
      setImagePreview('');
      setEditingBanner(null);
      showSnackbar(editingBanner ? 'Banner updated successfully!' : 'Banner created successfully!', 'success');
    } catch (error) {
      console.error('Error saving banner:', error);
      showSnackbar('Error saving banner. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      showSnackbar('Please select a valid image file.', 'warning');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleImageSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDropZoneClick = () => {
    handleChooseFile();
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageSelect(file);
    }
  };

  const handleChooseFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview('');
    setFormData(prev => ({ ...prev, imageUrl: '' }));
  };

  const parseBannerDate = (value, options = { endOfDay: false }) => {
    if (!value) return null;

    let parsedDate;

    if (value instanceof Date) {
      parsedDate = new Date(value.getTime());
    } else if (typeof value === 'string') {
      // Firestore may store ISO strings or plain YYYY-MM-DD from the date input
      if (value.includes('T')) {
        parsedDate = new Date(value);
      } else {
        parsedDate = new Date(`${value}T00:00:00`);
      }
    } else if (typeof value === 'number') {
      parsedDate = new Date(value);
    }

    if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    if (!value?.includes?.('T')) {
      if (options.endOfDay) {
        parsedDate.setHours(23, 59, 59, 999);
      } else {
        parsedDate.setHours(0, 0, 0, 0);
      }
    }

    return parsedDate;
  };

  const isBannerActive = (banner) => {
    const now = new Date();
    const startDate = parseBannerDate(banner.startDate, { endOfDay: false });
    const endDate = parseBannerDate(banner.endDate, { endOfDay: true });

    if ((banner.status || 'active') !== 'active') return false;
    if (startDate && now < startDate) return false;
    if (endDate && now > endDate) return false;
    return true;
  };

  if (loading) {
    return (
      <div className="banners-loading">
        <div className="loading-spinner"></div>
        <p>Loading banners...</p>
      </div>
    );
  }

  return (
    <div className="banners-page">
      <div className="page-header banners-header">
        <h2>My Banners</h2>
        <button className="btn btn-primary" onClick={handleAdd}>
          Add Banner
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content add-banner-modal">
            <div className="modal-header">
              <h3>{editingBanner ? 'Edit Banner' : 'Add Banner'}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowForm(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="banner-form">
              <div className="form-group">
                <label>Banner Image *</label>
                <div 
                  className={`drag-drop-zone ${dragActive ? 'drag-over' : ''} ${imagePreview ? 'has-preview' : ''}`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={handleDropZoneClick}
                >
                  {imagePreview ? (
                    <div className="banner-preview">
                      <img src={imagePreview} alt="Banner preview" />
                      <div className="preview-actions">
                        <button 
                          type="button" 
                          className="btn btn-sm btn-secondary"
                          onClick={removeImage}
                        >
                          Remove
                        </button>
                        <button 
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={handleChooseFile}
                        >
                          Change Image
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="drag-drop-content">
                      <div className="upload-icon">📁</div>
                      <p className="drag-drop-text">Drag & drop your images here</p>
                      <p className="drag-drop-subtext">or click to browse files</p>
                      <div className="supported-formats">Supported formats: JPG, PNG, GIF, WebP</div>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileInput}
                    ref={fileInputRef}
                    className="file-input"
                    hidden
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Link URL</label>
                <input
                  type="url"
                  name="linkUrl"
                  value={formData.linkUrl}
                  onChange={handleInputChange}
                  placeholder="Enter link URL"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : (editingBanner ? 'Update' : 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="banners-grid">
        {banners.length === 0 ? (
          <div className="no-banners">
            <div className="no-banners-icon">🖼️</div>
            <h3>No Banners Found</h3>
            <p>Create some banners to get started!</p>
            <button onClick={handleAdd} className="btn btn-primary">
              Create First Banner
            </button>
          </div>
        ) : (
          banners.map(banner => (
            <div key={banner.id} className="banner-card">
              <div className="banner-image">
                {banner.url ? (
                  <img src={banner.url} alt={banner.title} />
                ) : (
                  <div className="no-image">No Image</div>
                )}
                <div className={`status-badge ${isBannerActive(banner) ? 'active' : 'inactive'}`}>
                  {isBannerActive(banner) ? 'Live' : 'Inactive'}
                </div>
              </div>
              
              <div className="banner-info">
                <h3>{banner.title}</h3>
                <p className="position">{banner.position}</p>
                
                {banner.description && (
                  <p className="description">{banner.description}</p>
                )}
                
                {banner.link && (
                  <p className="link">
                    <span className="link-label">Link:</span>
                    <a href={`https://${banner.link}`} target="_blank" rel="noopener noreferrer" className="link-value">
                      {banner.link}
                    </a>
                  </p>
                )}
                
                <div className="banner-dates">
                  {banner.startDate && (
                    <div className="date-item">
                      <span className="date-label">Start:</span>
                      <span className="date-value">
                        {formatDisplayDate(banner.startDate)}
                      </span>
                    </div>
                  )}
                  {banner.endDate && (
                    <div className="date-item">
                      <span className="date-label">End:</span>
                      <span className="date-value">
                        {formatDisplayDate(banner.endDate)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="banner-actions">
                <button 
                  onClick={() => handleEdit(banner)}
                  className="btn btn-sm btn-secondary"
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDeleteClick(banner)}
                  className="btn btn-sm btn-danger"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-confirm-modal">
            <div className="delete-modal-header">
              <h2>Confirm Delete</h2>
              <button 
                className="close-btn"
                onClick={handleDeleteCancel}
              >
                ✕
              </button>
            </div>
            
            <div className="delete-modal-content">
              <div className="warning-icon">
                ⚠️
              </div>
              
              <div className="delete-message">
                <p>Are you sure you want to delete this banner? This will also delete all associated data.</p>
                <p className="warning-text">This action cannot be undone.</p>
              </div>
            </div>
            
            <div className="delete-modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={handleDeleteCancel}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
              >
                Delete Banner
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbar.show && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={hideSnackbar}
        />
      )}
    </div>
  );
};

export default Banners;
